// frontend/src/features/tasks/SnoozeTaskModal.tsx
/**
 * Modal for snoozing task instances with preset or custom durations.
 * Provides quick snooze options as pill buttons plus a date/time picker
 * for custom snooze times.
 */
import { useState, useEffect } from 'react';
import {
  Modal,
  Drawer,
  Button,
  Group,
  Stack,
  Text,
  Divider,
  Paper,
} from '@mantine/core';
import { DateTimePicker } from '@mantine/dates';
import { IconClock } from '@tabler/icons-react';
import type { TaskInstance } from '@/types/task';
import { useIsMobile } from '@/hooks/useIsMobile';

interface SnoozeTaskModalProps {
  opened: boolean;
  onClose: () => void;
  instance: TaskInstance | null;
  onSnooze: (snoozeUntil: string) => void;
  isLoading?: boolean;
}

interface SnoozePreset {
  label: string;
  value: string;
  getDate: () => Date;
}

function getSnoozePresets(): SnoozePreset[] {
  const now = new Date();

  return [
    {
      label: 'Later Today',
      value: 'later_today',
      getDate: () => {
        const later = new Date(now.getTime() + 4 * 60 * 60 * 1000);
        const evening = new Date(now);
        evening.setHours(18, 0, 0, 0);
        return later < evening ? later : evening;
      },
    },
    {
      label: 'Tomorrow',
      value: 'tomorrow',
      getDate: () => {
        const tomorrow = new Date(now);
        tomorrow.setDate(tomorrow.getDate() + 1);
        tomorrow.setHours(9, 0, 0, 0);
        return tomorrow;
      },
    },
    {
      label: 'This Weekend',
      value: 'this_weekend',
      getDate: () => {
        const saturday = new Date(now);
        const dayOfWeek = now.getDay();
        const daysUntilSaturday = (6 - dayOfWeek + 7) % 7 || 7;
        saturday.setDate(saturday.getDate() + daysUntilSaturday);
        saturday.setHours(10, 0, 0, 0);
        return saturday;
      },
    },
    {
      label: 'Next Week',
      value: 'next_week',
      getDate: () => {
        const monday = new Date(now);
        const dayOfWeek = now.getDay();
        const daysUntilMonday = (1 - dayOfWeek + 7) % 7 || 7;
        monday.setDate(monday.getDate() + daysUntilMonday);
        monday.setHours(9, 0, 0, 0);
        return monday;
      },
    },
  ];
}

function formatPresetTime(date: Date): string {
  const now = new Date();
  const isToday = date.toDateString() === now.toDateString();
  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const isTomorrow = date.toDateString() === tomorrow.toDateString();

  const timeStr = date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

  if (isToday) {
    return `Today ${timeStr}`;
  } else if (isTomorrow) {
    return `Tomorrow ${timeStr}`;
  } else {
    return date.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' }) + ` ${timeStr}`;
  }
}

export function SnoozeTaskModal({
  opened,
  onClose,
  instance,
  onSnooze,
  isLoading,
}: SnoozeTaskModalProps) {
  const isMobile = useIsMobile();
  const [customDate, setCustomDate] = useState<Date | null>(null);
  const presets = getSnoozePresets();

  // Reset custom date when modal opens
  useEffect(() => {
    if (opened) {
      setCustomDate(null);
    }
  }, [opened]);

  const handlePresetClick = (preset: SnoozePreset) => {
    onSnooze(preset.getDate().toISOString());
  };

  const handleCustomSubmit = () => {
    if (customDate) {
      onSnooze(customDate.toISOString());
    }
  };

  if (!instance) return null;

  const title = `Snooze: ${instance.name}`;

  const content = (
    <Stack gap="md">
      {/* Quick preset buttons */}
      <Stack gap="xs">
        <Text size="sm" fw={500}>Quick options</Text>
        <Group gap="xs">
          {presets.map((preset) => (
            <Button
              key={preset.value}
              variant="light"
              size="sm"
              onClick={() => handlePresetClick(preset)}
              loading={isLoading}
              leftSection={<IconClock size={14} />}
            >
              {preset.label}
            </Button>
          ))}
        </Group>
        <Text size="xs" c="dimmed">
          {presets.map((p) => `${p.label}: ${formatPresetTime(p.getDate())}`).join(' • ')}
        </Text>
      </Stack>

      <Divider label="or pick a specific time" labelPosition="center" />

      {/* Custom date/time picker */}
      <Paper p="sm" withBorder>
        <Stack gap="sm">
          <DateTimePicker
            label="Snooze until"
            placeholder="Select date and time"
            value={customDate}
            onChange={(val) => setCustomDate(val ? new Date(val) : null)}
            minDate={new Date()}
            clearable
            popoverProps={{ withinPortal: true }}
          />
          <Button
            onClick={handleCustomSubmit}
            loading={isLoading}
            disabled={!customDate}
            fullWidth
          >
            Snooze until {customDate ? formatPresetTime(customDate) : '...'}
          </Button>
        </Stack>
      </Paper>

      {/* Cancel button */}
      <Button variant="subtle" onClick={onClose} fullWidth>
        Cancel
      </Button>
    </Stack>
  );

  return isMobile ? (
    <Drawer
      opened={opened}
      onClose={onClose}
      title={title}
      position="bottom"
      size="85%"
      styles={{
        body: {
          paddingBottom: 'env(safe-area-inset-bottom, 16px)',
        },
      }}
    >
      {content}
    </Drawer>
  ) : (
    <Modal opened={opened} onClose={onClose} title={title} size="sm">
      {content}
    </Modal>
  );
}
